import React, { useState, useEffect } from "react";
import { Nav, Pagination } from "react-bootstrap";
import { AnimatePresence, motion } from "framer-motion";
import { getAllCoinsLite } from "../../../api/coin";
import { Link } from "react-router-dom";

const COIN_CATEGORIES = {
  0: "All",
  1: "Sport",
  2: "E-Sport",
  3: "MEME",
  4: "AI",
  5: "Exchange Based",
  6: "Stable",
  7: "Digital Assets",
};

const IcoListingFilter = () => {
  const [coins, setCoins] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 574);

  const itemsPerPage = 12;

  const fetchCoins = async (signal) => {
    try {
      const coinType = categoryFilter === 0 ? null : categoryFilter;

      const response = await getAllCoinsLite(
        currentPage,
        itemsPerPage,
        coinType,
        signal
      );

      const coinsWithCategory = response.data.map((coin) => ({
        ...coin,
        category: COIN_CATEGORIES[coin.coinType] || "Uncategorized",
      }));

      setCoins(coinsWithCategory);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") {
        return;
      }
      console.error("Error fetching coins:", error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchCoins(controller.signal);
    return () => controller.abort();
  }, [categoryFilter, currentPage]);

  const handleCategoryChange = (category) => {
    setCategoryFilter(category);
    setCurrentPage(1);
  };

  const handleRatingChange = (rating) => {
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];
    const visiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let endPage = startPage + visiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - visiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(
          <span key="start-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}>
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="end-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-4 pagination-gutter pagination-primary no-bg">
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() =>
              currentPage > 1 && handlePageChange(currentPage - 1)
            }>
            <i className="la la-angle-left" />
          </Link>
        </li>
        {items}
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() =>
              currentPage < totalPages && handlePageChange(currentPage + 1)
            }>
            <i className="la la-angle-right" />
          </Link>
        </li>
      </Pagination>
    );
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 574);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="row">
      {/* Category Tabs */}
      <div className="col-xl-12">
        <div className="card Infra">
          <div className="card-header border-0">
            <Nav
              as="ul"
              className="order nav-tabs justify-content-center"
              role="tablist">
              {Object.entries(COIN_CATEGORIES).map(([key, label]) => (
                <Nav.Item
                  as="li"
                  className="my-1"
                  role="presentation"
                  key={key}>
                  <Nav.Link
                    as="button"
                    eventKey={label}
                    active={categoryFilter === Number(key)}
                    onClick={() => handleCategoryChange(Number(key))}
                    className={
                      isMobile ? "border  rounded px-3 py-2" : ""
                    }>
                    {label} Token
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>
        </div>
      </div>

      {/* Filters + Grid */}
      <div className="col-xl-12">
        <div className="row">
          {/* Grid Content */}
          <div className="col-xl-12">
            <ul id="masonry" className="row">
              <AnimatePresence>
                {coins.map((coin) => (
                  <motion.li
                    layout
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="col-xl-3 col-md-6 px-3"
                    key={coin.id}>
                    <div className="card pull-up">
                      <div className="card-body align-items-center flex-wrap">
                        <div className="d-flex align-items-center mb-4">
                          <div className="ico-icon">
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${coin.logo}`}
                              alt={coin.name}
                              className="img-fluid"
                              style={{
                                width: 25,
                                height: 25,
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                          <div className="ms-4">
                            <h4 className="heading mb-0">{coin.name}</h4>
                            <span>
                              {coin.symbol} • {coin.category}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <p className="mb-0 fs-14 text-black">
                              {coin.totalSupply}
                            </p>
                            <span className="fs-12">Total Supply</span>
                          </div>
                          <div>
                            <p className="mb-0 fs-14 text-success">
                              {coin.maxSupply}
                            </p>
                            <span className="fs-12">Max Supply</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>

            {/* Pagination */}
            <div className="d-flex justify-content-center">
              {renderPagination()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IcoListingFilter;
